import { Container, Stack, Typography } from '@mui/material'
import { forwardRef } from 'react'
import {
  Button,
  MaskedInputField,
  MaskedInputFieldProps,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import { Loading } from '@/components/organisms'
import {
  Form,
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { CellFrame } from '@/core/architecture'

import { PaymentFormValues } from '../../PaymentCreate/template/PaymentCreateForm'
import { Action } from '../viewModel'

const schema: UseFormProps['schema'] = o => ({
  cardNumber: o.string().label(tLabel('paymentCreate.cardNumber')),
  cardHolder: o.string().label(tLabel('paymentCreate.cardHolder')),
  securityCode: o.string().label(tLabel('paymentCreate.securityCode')),
  expirationDate: o
    .string()
    .pattern(new RegExp('^(0[1-9]|1[0-2])/?([0-9]{2})$'))
    .label(tLabel('paymentCreate.expirationDate')),
})

export const RegisterPaymentMethodForm = ({
  onSubmitCreatePayment,
  submitting,
  loading,
}: {
  onSubmitCreatePayment: Action['onSubmitCreatePayment']
  loading: boolean
  submitting: boolean
}) => {
  const { t } = useTranslation('artistMembershipPaymentEdit')
  const { showMessage, showError } = useMessage()
  const {
    handleSubmit,
    register,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PaymentFormValues>({
    schema,
    defaultValues,
  })

  const onSubmit = handleSubmit(values =>
    onSubmitCreatePayment(values)
      .then(() => {
        showMessage(t('registerSuccess'))
        reset(defaultValues)
      })
      .catch(e => handleError<PaymentFormValues>(e, setError, showError))
  )

  if (loading) {
    return (
      <Container
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loading />
      </Container>
    )
  }

  return (
    <Stack sx={{ px: 5 }} spacing={2}>
      <Typography variant="subtitle2">
        {t('registerCreditCard', 'クレジットカード情報を登録してください。')}
      </Typography>
      <Form onSubmit={onSubmit} sx={{ mt: 4 }}>
        <MaskedInputCell
          type="text"
          inputProps={{
            pattern: '[0-9\\s]*',
            inputMode: 'numeric',
          }}
          mask="0000 0000 0000 0000"
          error={errors?.cardNumber}
          {...register('cardNumber')}
        />
        <TextInputCell
          type="text"
          error={errors?.cardHolder}
          {...register('cardHolder')}
        />
        <MaskedInputCell
          type="text"
          inputProps={{
            pattern: '[0-9\\s]*',
            inputMode: 'numeric',
          }}
          mask="0000"
          placeholder={t('')}
          error={errors?.securityCode}
          {...register('securityCode')}
        />
        <MaskedInputCell
          type="text"
          inputProps={{
            pattern: '[0-9/]*',
            inputMode: 'numeric',
          }}
          mask="00/00"
          error={errors?.expirationDate}
          {...register('expirationDate')}
        />
        <CellFrame sx={{ px: 4, py: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            type="submit"
            size="large"
            sx={{ width: 244 }}
            loading={submitting}
          >
            {t('register')}
          </Button>
        </CellFrame>
      </Form>
    </Stack>
  )
}

const defaultValues = {
  cardNumber: '',
  cardHolder: '',
  securityCode: '',
  expirationDate: '',
}
const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('artistMembershipPaymentEdit')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const MaskedInputCell = forwardRef(
  (props: Omit<MaskedInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('artistMembershipPaymentEdit')
    return (
      <CellFrame>
        <MaskedInputField
          {...props}
          ref={ref}
          t={t}
          placeholder={t(`${props.name}Placeholder`)}
        />
      </CellFrame>
    )
  }
)
